import {CM_LOGGER_APP} from "@app/common/services/logging"
import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatDialog} from "@angular/material/dialog"
import {ActivatedRoute, Router} from "@angular/router"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {IsLoadingDirective} from "@common/directives"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {AddUserDialogComponent} from "@platform/components/dialogs/add-user-dialog/add-user-dialog.component"
import {
    CreateStripeBillingPortalSessionGQL,
    CreateCheckoutSessionFromSettingsGQL,
    EndTrialNowFromSettingsGQL,
    GetOrganizationForSettingsGQL,
    GetOrganizationForSettingsQuery,
    SubscriptionPlansForSettingsGQL,
} from "@pages/settings/organization/organization-settings-page.generated"
import {AuthService} from "@app/common/services/auth/auth.service"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {environment} from "@environment"
import {AccountType} from "@generated"

interface SubscriptionPlan {
    productId: string
    productName: string
    productDescription: string | null
    features: string[]
    monthlyPrice: {
        id: string
        lookupKey: string | null
        currency: string
        unitAmount: number | null
        interval: string
    }
    yearlyPrice: {
        id: string
        lookupKey: string | null
        currency: string
        unitAmount: number | null
        interval: string
    }
    trialPeriodDays: number
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, LoadingSpinnerIconComponent, IsLoadingDirective],
    selector: "cm-organization-settings",
    styleUrls: ["./organization-settings-page.component.scss"],
    templateUrl: "./organization-settings-page.component.html",
})
export class OrganizationSettingsComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_APP)
    private auth = inject(AuthService)
    private dialog = inject(MatDialog)
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    private getOrganizationGql = inject(GetOrganizationForSettingsGQL)
    private createBillingPortalSessionGql = inject(CreateStripeBillingPortalSessionGQL)
    private subscriptionPlansGql = inject(SubscriptionPlansForSettingsGQL)
    private createCheckoutSessionGql = inject(CreateCheckoutSessionFromSettingsGQL)
    private endTrialNowGql = inject(EndTrialNowFromSettingsGQL)
    private permission = inject(PermissionsService)
    private notifications = inject(NotificationsService)

    $can = this.permission.$to

    // In the staff self-service preview the silo coerces only accountType; billing/members below are the
    // real backing org's data, so the template surfaces a notice when this is true.
    readonly $isSelfServicePreview = this.auth.$isSelfServicePreview

    // False on every backend except qa and prod, where the Stripe subscription webhook is registered.
    // Elsewhere checkout results never sync back into the org's subscription status, so the template
    // warns that billing state seen here is not authoritative.
    readonly stripeFullyIntegrated = environment.stripe.fullyIntegrated

    readonly $organization = signal<GetOrganizationForSettingsQuery["organization"] | null>(null)
    readonly $isLoading = signal(false)
    readonly $isLoadingBillingPortal = signal(false)
    readonly $isLoadingMembers = signal(false)
    readonly $checkoutSuccess = signal(false)

    // Plan selection
    readonly $subscriptionPlans = signal<SubscriptionPlan[]>([])
    readonly $selectedPlan = signal<SubscriptionPlan | null>(null)
    readonly $billingInterval = signal<"monthly" | "yearly">("monthly")
    readonly $isLoadingCheckout = signal(false)
    readonly $isUpgradingTrial = signal(false)

    readonly $savingsPercentage = computed(() => {
        const plans = this.$subscriptionPlans()
        if (plans.length === 0) return null
        const plan = plans[0]
        const monthlyAnnual = (plan.monthlyPrice.unitAmount || 0) * 12
        const yearly = plan.yearlyPrice.unitAmount || 0
        if (monthlyAnnual === 0) return null
        return Math.round(((monthlyAnnual - yearly) / monthlyAnnual) * 100)
    })

    $currentUser = this.auth.$user

    readonly $canAddUser = computed(() => {
        const organizationId = this.$organization()?.id
        return organizationId && this.$can().create.user({organization: {id: organizationId}})
    })

    async ngOnInit() {
        // Check for checkout result in query params
        const checkoutResult = this.route.snapshot.queryParamMap.get("checkout")
        if (checkoutResult === "success") {
            this.$checkoutSuccess.set(true)
            // Remove query param from URL
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {},
                replaceUrl: true,
            })
            // Load with retry to wait for webhook processing
            await this.loadOrganizationWithRetry()
        } else if (checkoutResult === "canceled") {
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {},
                replaceUrl: true,
            })
            await this.loadOrganization()
        } else {
            await this.loadOrganization()
        }
        await this.loadSubscriptionPlans()
    }

    /**
     * Load organization with retry logic to wait for Stripe webhook processing.
     * Retries up to 5 times with 2 second intervals.
     */
    private async loadOrganizationWithRetry(maxRetries = 5, delayMs = 2000): Promise<void> {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            await this.loadOrganization()

            // Check if subscription is now active
            if (this.$hasActiveSubscription()) {
                this.$checkoutSuccess.set(false)
                return
            }

            // Wait before retrying (except on last attempt)
            if (attempt < maxRetries - 1) {
                await new Promise((resolve) => setTimeout(resolve, delayMs))
            }
        }

        // After all retries, clear the loading state
        this.$checkoutSuccess.set(false)
    }

    private async loadSubscriptionPlans() {
        try {
            const result = await this.subscriptionPlansGql.fetch().toPromise()
            if (result?.data?.subscriptionPlans) {
                const plans = JSON.parse(result.data.subscriptionPlans) as SubscriptionPlan[]
                this.$subscriptionPlans.set(plans)
                if (plans.length > 0) {
                    this.$selectedPlan.set(plans[0])
                }
            }
        } catch (error) {
            this.logger.error("Failed to load subscription plans:", error)
        }
    }

    private async loadOrganization() {
        const organizationId = await this.auth.silo.$silo()?.organization?.id
        if (!organizationId) {
            this.logger.error("No organization ID provided")
            return
        }

        this.$isLoading.set(true)
        try {
            const result = await fetchThrowingErrors(this.getOrganizationGql)({
                organizationId,
            })
            this.$organization.set(result.organization)
        } catch (error) {
            this.logger.error("Failed to load organization:", error)
        } finally {
            this.$isLoading.set(false)
        }
    }

    async openStripeBillingPortal() {
        const org = this.$organization()
        if (!org?.id) {
            this.logger.error("No organization available")
            return
        }

        this.$isLoadingBillingPortal.set(true)
        try {
            const result = await mutateThrowingErrors(this.createBillingPortalSessionGql)({
                organizationId: org.id,
                returnUrlPath: `/settings/organization`,
            })

            if (result.createStripeBillingPortalSession) {
                window.open(result.createStripeBillingPortalSession, "_blank")
            }
        } catch (error) {
            this.logger.error("Failed to create billing portal session:", error)
        } finally {
            this.$isLoadingBillingPortal.set(false)
        }
    }

    getStatusDisplay(status: string | null | undefined): string {
        if (!status) return "Unknown"

        const statusMap: Record<string, string> = {
            active: "Active",
            canceled: "Canceled",
            incomplete: "Incomplete",
            incomplete_expired: "Incomplete",
            past_due: "Payment due",
            trialing: "Trial",
            unpaid: "Payment due",
        }

        return statusMap[status.toLowerCase()] || status
    }

    getStatusClass(status: string | null | undefined): string {
        if (!status) return "status-unknown"

        const classMap: Record<string, string> = {
            active: "status-active",
            trialing: "status-active",
            canceled: "status-canceled",
            incomplete: "status-warning",
            incomplete_expired: "status-warning",
            past_due: "status-warning",
            unpaid: "status-warning",
        }

        return classMap[status.toLowerCase()] || "status-unknown"
    }

    formatDate(dateString: string | null | undefined): string {
        if (!dateString) return "Unknown"

        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    readonly $planName = computed(() => {
        const org = this.$organization()
        if (!org) return "Free"
        if (org.stripeProductName) return org.stripeProductName
        return "Paid Plan"
    })

    getTrialDaysRemaining(trialEndsAt: string | null | undefined): number | null {
        if (!trialEndsAt) return null
        const endDate = new Date(trialEndsAt)
        const now = new Date()
        const diffTime = endDate.getTime() - now.getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        return diffDays > 0 ? diffDays : null
    }

    getTrialUrgencyClass(daysRemaining: number | null): string {
        if (daysRemaining === null) return ""
        if (daysRemaining <= 3) return "trial-warning"
        return ""
    }

    readonly $isOnTrial = computed(() => {
        const org = this.$organization()
        if (!org) return false
        if (org.stripeSubscriptionStatus?.toLowerCase() === "trialing") return true
        const daysRemaining = this.getTrialDaysRemaining(org.trialEndsAt)
        return daysRemaining !== null && daysRemaining > 0
    })

    readonly $trialEndDate = computed(() => {
        const org = this.$organization()
        if (!org) return null
        if (org.stripeSubscriptionStatus?.toLowerCase() === "trialing" && org.stripeCurrentPeriodEnd) {
            return org.stripeCurrentPeriodEnd
        }
        return org.trialEndsAt ?? null
    })

    isCurrentUser(userId: string): boolean {
        return this.$currentUser()?.id === userId
    }

    openAddUserDialog() {
        const org = this.$organization()
        if (!org?.id) {
            this.logger.error("No organization available")
            return
        }

        const dialogRef = this.dialog.open(AddUserDialogComponent)

        // Pass the organization ID to the dialog component
        dialogRef.componentInstance.$organizationId.set(org.id)

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                // User was successfully invited, refresh the organization data
                await this.loadOrganization()
            }
        })
    }

    // Whether a live Stripe subscription exists, i.e. one managed via the billing portal rather than a new checkout.
    // past_due and unpaid count: the subscription still exists and the user fixes payment in the portal (a fresh
    // checkout would create a second subscription), even though the account itself is already treated as Free.
    readonly $hasActiveSubscription = computed(() => {
        const org = this.$organization()
        if (!org) return false
        const activeStatuses = ["active", "trialing", "past_due", "unpaid"]
        const status = org.stripeSubscriptionStatus?.toLowerCase()
        return !!status && activeStatuses.includes(status)
    })

    // A failed payment on a still-existing subscription. The subscription guard no longer redirects these orgs,
    // so this page is the only place the problem surfaces.
    readonly $hasPaymentProblem = computed(() => {
        const status = this.$organization()?.stripeSubscriptionStatus?.toLowerCase()
        return status === "past_due" || status === "unpaid"
    })

    // Every self-service org that isn't paying or trialing, including canceled/lapsed subscriptions.
    readonly $isFreePlan = computed(() => this.$organization()?.accountType === AccountType.SelfServiceFree)

    /**
     * Redirect to Stripe checkout to start a subscription.
     */
    getCurrencySymbol(currency: string): string {
        const symbols: Record<string, string> = {
            eur: "€",
            usd: "$",
            gbp: "£",
        }
        return symbols[currency.toLowerCase()] ?? currency.toUpperCase()
    }

    /**
     * End the free trial immediately and start the paid subscription now, rather than waiting for the
     * trial to elapse. Polls until the subscription-updated webhook flips the status to active, then
     * refreshes the silo so the derived accountType (now paid) applies across the app.
     */
    async upgradeNow(): Promise<void> {
        this.$isUpgradingTrial.set(true)
        try {
            await mutateThrowingErrors(this.endTrialNowGql)({})
            const becameActive = await this.pollUntilActive()
            if (becameActive) {
                await this.auth.silo.load()
                this.notifications.showInfo("Your subscription is now active.")
            } else {
                this.notifications.showInfo("Your payment is processing; this can take a moment to take effect.")
            }
        } catch (error) {
            this.notifications.showError(error instanceof Error ? error.message : "Failed to start your subscription")
        } finally {
            this.$isUpgradingTrial.set(false)
        }
    }

    /**
     * Poll the organization until the subscription is specifically active. $hasActiveSubscription treats
     * trialing as active, so it can't detect the trial→active transition; this checks the raw status.
     */
    private async pollUntilActive(maxRetries = 6, delayMs = 2000): Promise<boolean> {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            await this.loadOrganization()
            if (this.$organization()?.stripeSubscriptionStatus?.toLowerCase() === "active") {
                return true
            }
            if (attempt < maxRetries - 1) {
                await new Promise((resolve) => setTimeout(resolve, delayMs))
            }
        }
        return false
    }

    async redirectToStripeCheckout(): Promise<void> {
        const selectedPlan = this.$selectedPlan()
        if (!selectedPlan) {
            this.notifications.showError("Please select a plan")
            return
        }

        this.$isLoadingCheckout.set(true)

        try {
            const price = this.$billingInterval() === "monthly" ? selectedPlan.monthlyPrice : selectedPlan.yearlyPrice
            const priceLookupKey = price.lookupKey

            if (!priceLookupKey) {
                throw new Error("Price lookup key not found")
            }

            const baseUrl = window.location.origin
            const successUrl = `${baseUrl}/settings/organization?checkout=success`
            const cancelUrl = `${baseUrl}/settings/organization?checkout=canceled`

            const result = await mutateThrowingErrors(this.createCheckoutSessionGql)({
                priceLookupKey,
                successUrl,
                cancelUrl,
            })

            if (result?.createCheckoutSession) {
                // Redirect to Stripe Checkout
                window.location.href = result.createCheckoutSession
            }
        } catch (error) {
            this.notifications.showError(error instanceof Error ? error.message : "Failed to create checkout session")
        } finally {
            this.$isLoadingCheckout.set(false)
        }
    }
}
